import React, { useState } from "react";
import { Play, Pause, Volume2, VolumeX, Heart, Share2, Radio, Circle, Square, Sliders, Moon, MoonStar, Bell, BellRing } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import EQPanel from "@/components/EQPanel";
import SleepTimerPanel from "@/components/SleepTimerPanel";
import AlarmPanel from "@/components/AlarmPanel";
import Equalizer from "@/components/Equalizer";

const fmtTime = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function Player() {
  const {
    current, playing, loading, volume, muted,
    togglePlay, changeVolume, toggleMute,
    startRecording, stopRecording, recording, recordSeconds,
    shareStation, toggleFavorite, isFavorite,
    nowPlaying,
    sleepTimerMinutes, sleepRemaining,
    alarm,
  } = usePlayer();
  const { t } = useI18n();

  const [showEQ, setShowEQ] = useState(false);
  const [showSleep, setShowSleep] = useState(false);
  const [showAlarm, setShowAlarm] = useState(false);
  const closeAll = () => { setShowEQ(false); setShowSleep(false); setShowAlarm(false); };

  const fav = current ? isFavorite(current.stationuuid) : false;
  const alarmActive = !!(alarm && alarm.enabled);

  return (
    <>
      <div data-testid="player-bar" className="glass fixed bottom-0 left-0 right-0 z-40 border-t border-white/5">
        <div className="px-4 md:px-6 py-3 flex items-center gap-4">
          {/* Station info */}
          <div className="flex items-center gap-3 min-w-0 w-[280px] lg:w-[320px]">
            <div className="w-12 h-12 shrink-0 bg-black border border-[var(--voix-border)] flex items-center justify-center overflow-hidden">
              {current?.favicon ? (
                <img src={current.favicon} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <Radio size={20} className="text-slate-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              {current ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${playing ? "bg-green-500 onair-dot" : "bg-slate-600"}`} />
                    <span className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-slate-500">
                      {loading ? t("player.tuning").toUpperCase() : playing ? t("player.on_air").toUpperCase() : t("player.paused").toUpperCase()}
                    </span>
                  </div>
                  <div data-testid="player-station-name" className="font-heading font-bold text-sm truncate text-white">{current.name}</div>
                  {nowPlaying ? (
                    <div data-testid="player-nowplaying" className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-300 truncate" title={nowPlaying}>
                      ♪ {nowPlaying}
                    </div>
                  ) : (
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 truncate">
                      {current.country}{current.codec ? ` · ${current.codec}` : ""}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-slate-600">{t("player.idle").toUpperCase()}</div>
                  <div className="font-heading font-bold text-sm text-slate-500">{t("player.pick")}</div>
                </>
              )}
            </div>
          </div>

          {/* Transport */}
          <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap justify-center">
              <button
                data-testid="player-play-btn"
                onClick={togglePlay}
                disabled={!current}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition ${
                  current ? "bg-cyan-500 text-black hover:bg-cyan-400" : "bg-white/5 text-slate-600"
                }`}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : playing ? (
                  <Pause size={18} />
                ) : (
                  <Play size={18} className="translate-x-0.5" />
                )}
              </button>

              <button
                data-testid="player-record-btn"
                onClick={recording ? stopRecording : startRecording}
                disabled={!current}
                className={`h-9 px-3 border flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition ${
                  recording ? "border-red-500 text-red-400 bg-red-500/10" : "border-[var(--voix-border)] text-slate-400 hover:border-pink-500 hover:text-pink-400"
                }`}
              >
                {recording ? <Square size={11} fill="currentColor" /> : <Circle size={11} fill="currentColor" />}
                <span>{recording ? `${t("player.rec").toUpperCase()} ${fmtTime(recordSeconds)}` : t("player.rec").toUpperCase()}</span>
              </button>

              <button
                data-testid="player-eq-btn"
                onClick={() => { closeAll(); setShowEQ(true); }}
                className={`h-9 px-3 border flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition ${
                  showEQ ? "border-yellow-500 text-yellow-400 bg-yellow-500/10" : "border-[var(--voix-border)] text-slate-400 hover:border-yellow-500 hover:text-yellow-400"
                }`}
              >
                <Sliders size={11} />
                <span>{t("player.eq").toUpperCase()}</span>
              </button>

              <button
                data-testid="player-sleep-btn"
                onClick={() => { closeAll(); setShowSleep(true); }}
                className={`h-9 px-3 border flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition ${
                  sleepTimerMinutes > 0 ? "border-cyan-500 text-cyan-300 bg-cyan-500/10"
                    : showSleep ? "border-cyan-500 text-cyan-300"
                    : "border-[var(--voix-border)] text-slate-400 hover:border-cyan-500 hover:text-cyan-300"
                }`}
              >
                {sleepTimerMinutes > 0 ? <MoonStar size={11} /> : <Moon size={11} />}
                <span>{sleepTimerMinutes > 0 ? `${Math.floor(sleepRemaining/60)}:${String(sleepRemaining%60).padStart(2,"0")}` : t("player.sleep").toUpperCase()}</span>
              </button>

              <button
                data-testid="player-alarm-btn"
                onClick={() => { closeAll(); setShowAlarm(true); }}
                className={`h-9 px-3 border flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition ${
                  alarmActive ? "border-pink-500 text-pink-300 bg-pink-500/10"
                    : showAlarm ? "border-pink-500 text-pink-300"
                    : "border-[var(--voix-border)] text-slate-400 hover:border-pink-500 hover:text-pink-300"
                }`}
              >
                {alarmActive ? <BellRing size={11} /> : <Bell size={11} />}
                <span>{alarmActive ? alarm.time : t("player.alarm").toUpperCase()}</span>
              </button>

              <button
                data-testid="player-share-btn"
                onClick={() => shareStation()}
                disabled={!current}
                className="h-9 px-3 border border-[var(--voix-border)] text-slate-400 hover:border-cyan-500 hover:text-cyan-400 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition disabled:opacity-40"
              >
                <Share2 size={11} />
                <span className="hidden md:inline">{t("player.share").toUpperCase()}</span>
              </button>

              <button
                data-testid="player-fav-btn"
                onClick={() => current && toggleFavorite(current)}
                disabled={!current}
                className={`h-9 px-3 border flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest transition disabled:opacity-40 ${
                  fav ? "border-pink-500 text-pink-400 bg-pink-500/10" : "border-[var(--voix-border)] text-slate-400 hover:border-pink-500 hover:text-pink-400"
                }`}
              >
                <Heart size={11} fill={fav ? "currentColor" : "none"} />
                <span className="hidden md:inline">{t("player.fav").toUpperCase()}</span>
              </button>
            </div>

            <div className="w-full max-w-md">
              <Equalizer />
            </div>
          </div>

          <div className="flex items-center gap-2 w-[160px] justify-end">
            <button data-testid="player-mute-btn" onClick={toggleMute} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-cyan-400 transition">
              {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input
              data-testid="player-volume"
              type="range" min={0} max={1} step={0.01}
              value={muted ? 0 : volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              className="voix-slider w-full"
            />
          </div>
        </div>
      </div>

      {showEQ && <EQPanel onClose={() => setShowEQ(false)} />}
      {showSleep && <SleepTimerPanel onClose={() => setShowSleep(false)} />}
      {showAlarm && <AlarmPanel onClose={() => setShowAlarm(false)} />}
    </>
  );
}
